import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  ClipboardCheck,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  TrendingUp,
  Clock,
  Play,
  ArrowRight,
  Loader2,
  MapPin,
  Target,
  Activity,
  Gauge,
  Shield,
  CalendarDays,
  X,
  AlertOctagon,
  Download
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { ExportButtons } from '../components/ExportButtons';

import { API_BASE } from '../utils/apiClient';

const API = API_BASE;

// Format action title to display "Non conforme" instead of "NOK"
const formatActionTitle = (title, t) => {
  if (!title) return '';
  return title
    .replace(/^NOK\s*:\s*/i, `${t('actions.nonConforming')}: `)
    .replace(/^Non conforme\s*:\s*/i, `${t('actions.nonConforming')}: `);
};

const Dashboard = () => {
  const { getAuthHeaders, isDemoReadonly } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [leanStats, setLeanStats] = useState(null);
  const [trsStats, setTrsStats] = useState(null);
  const [safetyStats, setSafetyStats] = useState(null);
  const [sites, setSites] = useState([]);
  const [zones, setZones] = useState([]);
  const [selectedSite, setSelectedSite] = useState('all');
  const [selectedZone, setSelectedZone] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState(isDemoReadonly() ? 'year2025' : '7');
  const [customDateRange, setCustomDateRange] = useState(null); // { from: Date, to: Date }
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toursCompliance, setToursCompliance] = useState(null); // Suivi conformité tournées

  // Default empty stats to prevent crashes
  const defaultStats = {
    total_tours: 0,
    total_ok: 0,
    total_nok: 0,
    total_na: 0,
    compliance_rate: 0,
    nok_rate: 0,
    actions_due: 0,
    top_nok_zones: [],
    recent_actions: []
  };

  const defaultLeanStats = {
    average_5s_score: 0,
    actions_closed_on_time_percentage: 0,
    total_tours_completed: 0,
    average_days_to_close: 0
  };

  const defaultTrsStats = {
    average_trs: 0,
    trend: [],
    entries_count: 0
  };

  const defaultSafetyStats = {
    near_misses: 0,
    with_stoppage: 0,
    frequency_rate: 0,
    lost_days: 0
  };

  // Period options
  const periodOptions = [
    { value: '7', labelKey: 'dashboard.period7', subtitleKey: 'dashboard.performanceOf7' },
    { value: '30', labelKey: 'dashboard.period30', subtitleKey: 'dashboard.performanceOf30' },
    { value: 'month', labelKey: 'dashboard.currentMonth', subtitleKey: 'dashboard.performanceOfMonth' },
    { value: '365', labelKey: '12 derniers mois', subtitleKey: 'Performance des 12 derniers mois' },
    { value: 'year2025', labelKey: 'Annee 2025', subtitleKey: 'Performance annuelle 2025' },
    { value: 'custom', labelKey: 'Periode personnalisee', subtitleKey: 'dashboard.customPeriod' }
  ];

  // Calculate days for current month
  const getDaysForPeriod = () => {
    if (selectedPeriod === 'month') {
      const now = new Date();
      return now.getDate(); // Days elapsed in current month
    }
    if (selectedPeriod === 'year2025') {
      return null; // Will use date range instead
    }
    if (selectedPeriod === 'custom' && customDateRange?.from && customDateRange?.to) {
      return null; // Will use date range instead
    }
    return parseInt(selectedPeriod);
  };

  const getCurrentSubtitle = () => {
    if (selectedPeriod === 'custom' && customDateRange?.from && customDateRange?.to) {
      return `Du ${format(customDateRange.from, 'dd/MM/yyyy', { locale: fr })} au ${format(customDateRange.to, 'dd/MM/yyyy', { locale: fr })}`;
    }
    const selected = periodOptions.find(p => p.value === selectedPeriod);
    return selected ? t(selected.subtitleKey) : t('dashboard.title');
  };

  // Clear custom date range
  const clearCustomDate = () => {
    setCustomDateRange(null);
    setSelectedPeriod('7');
  };

  // Handle date selection from calendar
  const handleDateSelect = (range) => {
    if (range?.from && range?.to) {
      setCustomDateRange(range);
      setSelectedPeriod('custom');
      setCalendarOpen(false);
    } else if (range?.from) {
      setCustomDateRange({ from: range.from, to: range.from });
    }
  };

  // Get filtered zones based on selected site
  const getFilteredZones = () => {
    if (selectedSite === 'all') return zones;
    // Get all descendant site IDs
    const getAllDescendants = (siteId) => {
      const descendants = [siteId];
      const children = sites.filter(s => s.parent_id === siteId);
      children.forEach(child => {
        descendants.push(...getAllDescendants(child.id));
      });
      return descendants;
    };
    const siteIds = getAllDescendants(selectedSite);
    return zones.filter(z => siteIds.includes(z.site_id));
  };

  // Reset zone selection when site changes
  useEffect(() => {
    setSelectedZone('all');
  }, [selectedSite]);

  useEffect(() => {
    fetchData();
  }, [selectedSite, selectedZone, selectedPeriod, customDateRange]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const days = getDaysForPeriod();
      const filterParams = {};
      
      // Add date range if custom period is selected
      if (selectedPeriod === 'year2025') {
        filterParams.start_date = '2025-01-01';
        filterParams.end_date = '2025-12-31';
      } else if (selectedPeriod === 'custom' && customDateRange?.from && customDateRange?.to) {
        filterParams.start_date = format(customDateRange.from, 'yyyy-MM-dd');
        filterParams.end_date = format(customDateRange.to, 'yyyy-MM-dd');
      } else if (days) {
        filterParams.days = days;
      }
      
      if (selectedZone !== 'all') {
        filterParams.zone_id = selectedZone;
      } else if (selectedSite !== 'all') {
        filterParams.site_id = selectedSite;
      }
      // Only add days if not using date range period
      if (selectedPeriod !== 'custom' && selectedPeriod !== 'year2025' && days) {
        filterParams.days = days;
      }
      
      const queryString = Object.keys(filterParams).length > 0 
        ? '?' + Object.entries(filterParams).map(([k,v]) => `${k}=${v}`).join('&')
        : `?days=${days}`;
      
      // Fetch data with individual error handling
      const results = await Promise.allSettled([
        axios.get(`${API}/dashboard/stats`, { params: filterParams, headers: getAuthHeaders() }),
        axios.get(`${API}/sites`, { headers: getAuthHeaders() }),
        axios.get(`${API}/zones`, { headers: getAuthHeaders() }),
        axios.get(`${API}/lean/indicators${queryString}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/trs/stats${queryString}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/safety/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/dashboard/tours-compliance`, { headers: getAuthHeaders() })
      ]);
      
      // Process results with fallbacks
      setStats(results[0].status === 'fulfilled' ? results[0].value.data : defaultStats);
      setSites(results[1].status === 'fulfilled' ? results[1].value.data || [] : []);
      setZones(results[2].status === 'fulfilled' ? results[2].value.data || [] : []);
      setLeanStats(results[3].status === 'fulfilled' ? results[3].value.data : defaultLeanStats);
      setTrsStats(results[4].status === 'fulfilled' ? results[4].value.data : defaultTrsStats);
      setSafetyStats(results[5].status === 'fulfilled' ? results[5].value.data : defaultSafetyStats);
      setToursCompliance(results[6].status === 'fulfilled' ? results[6].value.data : null);
      
      // Check if all failed
      const allFailed = results.every(r => r.status === 'rejected');
      if (allFailed) {
        setError('Impossible de charger les données. Veuillez réessayer.');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Une erreur est survenue lors du chargement.');
      // Set default values to prevent crashes
      setStats(defaultStats);
      setLeanStats(defaultLeanStats);
      setTrsStats(defaultTrsStats);
      setSafetyStats(defaultSafetyStats);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      haute: 'bg-red-600 text-white',
      moyenne: 'bg-amber-500 text-white',
      faible: 'bg-gray-500 text-white'
    };
    return <Badge className={`${styles[priority] || styles.moyenne} shadow-sm font-bold`}>{priority?.toUpperCase()}</Badge>;
  };

  const getStatusBadge = (status) => {
    const styles = {
      A_FAIRE: 'bg-gray-200 text-gray-800',
      EN_COURS: 'bg-blue-600 text-white',
      FAIT: 'bg-emerald-600 text-white',
      BLOQUE: 'bg-red-600 text-white'
    };
    const labels = {
      A_FAIRE: 'À FAIRE',
      EN_COURS: 'EN COURS',
      FAIT: 'FAIT',
      BLOQUE: 'BLOQUÉ'
    };
    return <Badge className={`${styles[status] || styles.A_FAIRE} font-bold`}>{labels[status] || status}</Badge>;
  };

  const isOverdue = (deadline) => {
    return new Date(deadline) < new Date();
  };

  const pieData = stats ? [
    { name: t('dashboard.conformity'), value: stats.ok_rate || 0, color: '#059669' },
    { name: t('dashboard.nonConformity'), value: stats.nok_rate || 0, color: '#DC2626' }
  ] : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-10 h-10 animate-spin text-[#0E346E]" />
      </div>
    );
  }

  // Use stats with fallback to defaults
  const safeStats = stats || defaultStats;
  const safeLeanStats = leanStats || defaultLeanStats;
  const safeTrsStats = trsStats || defaultTrsStats;
  const safeSafetyStats = safetyStats || defaultSafetyStats;

  return (
    <div className="p-4 md:p-8 space-y-6 fade-in" data-testid="dashboard">
      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          <span className="text-red-800">{error}</span>
          <Button variant="outline" size="sm" onClick={fetchData} className="ml-auto">
            Réessayer
          </Button>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-4xl font-bold text-gray-900 font-['Barlow_Condensed'] tracking-wide">{t('dashboard.title')}</h1>
          <p className="text-gray-500 mt-1">{getCurrentSubtitle()}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Export Buttons */}
          <ExportButtons module="dashboard" filters={{
            site_id: selectedSite !== 'all' ? selectedSite : undefined,
            start_date: customDateRange?.from ? format(customDateRange.from, 'yyyy-MM-dd') : undefined,
            end_date: customDateRange?.to ? format(customDateRange.to, 'yyyy-MM-dd') : undefined,
          }} />
          {/* Period Filter */}
          <Select value={selectedPeriod} onValueChange={(value) => {
            if (value !== 'custom') {
              setCustomDateRange(null);
            }
            setSelectedPeriod(value);
          }}>
            <SelectTrigger className="w-44 h-12 border-2" data-testid="period-filter">
              <SelectValue placeholder={t('common.period')} />
            </SelectTrigger>
            <SelectContent>
              {periodOptions.filter(o => o.value !== 'custom').map((option) => (
                <SelectItem key={option.value} value={option.value}>{t(option.labelKey)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Calendar Date Picker */}
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button 
                variant={customDateRange ? "default" : "outline"} 
                className={`h-12 ${customDateRange ? 'bg-[#0E346E] text-white' : ''}`}
                data-testid="calendar-btn"
              >
                <CalendarDays className="w-5 h-5 mr-2" />
                {customDateRange?.from ? (
                  customDateRange.to && customDateRange.from !== customDateRange.to ? (
                    <span className="text-sm">
                      {format(customDateRange.from, 'dd/MM')} - {format(customDateRange.to, 'dd/MM')}
                    </span>
                  ) : (
                    <span className="text-sm">{format(customDateRange.from, 'dd/MM/yyyy')}</span>
                  )
                ) : (
                  <span>Calendrier</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <div className="p-3 border-b">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">Sélectionner une période</span>
                  {customDateRange && (
                    <Button variant="ghost" size="sm" onClick={clearCustomDate}>
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              <Calendar
                mode="range"
                selected={customDateRange}
                onSelect={handleDateSelect}
                locale={fr}
                numberOfMonths={1}
                disabled={{ after: new Date() }}
                className="rounded-md"
              />
              <div className="p-3 border-t flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setCalendarOpen(false)}>
                  Annuler
                </Button>
                <Button 
                  size="sm" 
                  onClick={() => setCalendarOpen(false)}
                  disabled={!customDateRange?.from || !customDateRange?.to}
                  className="bg-[#0E346E]"
                >
                  Appliquer
                </Button>
              </div>
            </PopoverContent>
          </Popover>
          
          {/* Site Filter */}
          <Select value={selectedSite} onValueChange={setSelectedSite}>
            <SelectTrigger className="w-48 h-12 border-2" data-testid="site-filter">
              <SelectValue placeholder={t('common.allUnits')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.allUnits')}</SelectItem>
              {sites
                .filter(s => !s.parent_id) // Only root units
                .map((site) => {
                  const children = sites.filter(s => s.parent_id === site.id);
                  return (
                    <React.Fragment key={site.id}>
                      <SelectItem value={site.id}>
                        <span className="font-semibold">{site.name}</span>
                      </SelectItem>
                      {children.map(child => {
                        const grandchildren = sites.filter(s => s.parent_id === child.id);
                        return (
                          <React.Fragment key={child.id}>
                            <SelectItem value={child.id}>
                              <span className="ml-3 text-gray-600">↳ {child.name}</span>
                            </SelectItem>
                            {grandchildren.map(gc => (
                              <SelectItem key={gc.id} value={gc.id}>
                                <span className="ml-6 text-gray-500">↳ {gc.name}</span>
                              </SelectItem>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
            </SelectContent>
          </Select>
          {/* Zone Filter */}
          {selectedSite !== 'all' && getFilteredZones().length > 0 && (
            <Select value={selectedZone} onValueChange={setSelectedZone}>
              <SelectTrigger className="w-44 h-12 border-2" data-testid="zone-filter">
                <SelectValue placeholder={t('actions.zone')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')} ({getFilteredZones().length} zones)</SelectItem>
                {getFilteredZones().map((zone) => (
                  <SelectItem key={zone.id} value={zone.id}>{zone.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Link to="/gemba">
            <Button className="h-12 bg-[#0E346E] hover:bg-[#0A2850] shadow-md px-6" data-testid="start-tour-btn">
              <Play className="w-5 h-5 mr-2" />
              {t('dashboard.start')}
            </Button>
          </Link>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="border-0 shadow-md bg-white" data-testid="kpi-tours">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium uppercase tracking-wide">
                  {t('dashboard.toursCount')} ({selectedPeriod === 'month' ? t('dashboard.month') : selectedPeriod === 'year2025' ? '2025' : selectedPeriod === 'custom' ? t('dashboard.custom') || 'Personnalise' : `${getDaysForPeriod()}${t('dashboard.days').charAt(0)}`})
                </p>
                <p className="text-4xl font-bold text-gray-900 mt-1">{stats?.tours_count || 0}</p>
              </div>
              <div className="w-14 h-14 bg-[#0E346E] rounded-xl flex items-center justify-center shadow-md">
                <ClipboardCheck className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-white" data-testid="kpi-ok">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium uppercase tracking-wide">{t('dashboard.okRate')}</p>
                <p className="text-4xl font-bold text-emerald-600 mt-1">{stats?.ok_rate || 0}%</p>
              </div>
              <div className="w-14 h-14 bg-emerald-600 rounded-xl flex items-center justify-center shadow-md">
                <CheckCircle2 className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-white" data-testid="kpi-nok">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium uppercase tracking-wide">{t('dashboard.nokRate')}</p>
                <p className="text-4xl font-bold text-red-600 mt-1">{stats?.nok_rate || 0}%</p>
              </div>
              <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center shadow-md">
                <XCircle className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-white" data-testid="kpi-overdue">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium uppercase tracking-wide">{t('dashboard.overdue')}</p>
                <p className="text-4xl font-bold text-amber-600 mt-1">{stats?.overdue_actions || 0}</p>
              </div>
              <div className="w-14 h-14 bg-amber-500 rounded-xl flex items-center justify-center shadow-md">
                <AlertTriangle className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Critical Escalation KPI - NEW */}
        <Card 
          className={`border-0 shadow-md ${(stats?.critical_escalated_count || 0) > 0 ? 'bg-red-50 border-2 border-red-300 animate-pulse' : 'bg-white'}`} 
          data-testid="kpi-critical-escalation"
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium uppercase tracking-wide">
                  {t('dashboard.criticalEscalation') || 'Escalade J+7'}
                </p>
                <p className={`text-4xl font-bold mt-1 ${(stats?.critical_escalated_count || 0) > 0 ? 'text-red-700' : 'text-gray-400'}`}>
                  {stats?.critical_escalated_count || 0}
                </p>
                {(stats?.critical_escalated_count || 0) > 0 && (
                  <p className="text-xs text-red-600 font-semibold mt-1">CRITIQUE</p>
                )}
              </div>
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center shadow-md ${(stats?.critical_escalated_count || 0) > 0 ? 'bg-red-700' : 'bg-gray-300'}`}>
                <AlertOctagon className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Critical Escalation Alert Banner - Show if there are critical actions */}
      {(stats?.critical_escalated_count || 0) > 0 && (
        <Card className="border-2 border-red-400 bg-red-50 shadow-lg" data-testid="critical-escalation-alert">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed'] text-red-800 tracking-wide flex items-center gap-2">
              <AlertOctagon className="w-5 h-5" />
              {t('dashboard.criticalAlertTitle') || 'ACTIONS EN ESCALADE CRITIQUE (J+7)'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-red-700 mb-3">
              {t('dashboard.criticalAlertDescription') || 'Ces actions sont en retard de plus de 7 jours et nécessitent une attention immédiate.'}
            </p>
            <div className="space-y-2">
              {stats?.critical_escalated_actions?.map((action, idx) => (
                <div key={action.id || idx} className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-200">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 truncate">{action.title}</p>
                    <p className="text-xs text-gray-500">{action.zone_name}</p>
                  </div>
                  <div className="text-right">
                    <Badge className="bg-red-600 text-white">
                      +{action.days_overdue} {t('dashboard.days') || 'jours'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <Link to="/actions">
                <Button variant="outline" className="border-red-400 text-red-700 hover:bg-red-100">
                  {t('dashboard.viewAllActions') || 'Voir toutes les actions'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conformity Pie Chart */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed'] text-gray-900 tracking-wide">{t('dashboard.okNokDistribution')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}%`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Top Non-Conforming Zones */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed'] text-gray-900 tracking-wide">{t('dashboard.topNokZones')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {stats?.top_nok_zones?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.top_nok_zones} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis type="number" />
                    <YAxis dataKey="zone_name" type="category" width={100} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="nok_count" fill="#DC2626" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400">
                  <p>{t('common.noData')}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lean, TRS & Safety Indicators */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lean Indicators */}
        <Card className="border-0 shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed'] text-gray-900 tracking-wide flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-600" />
              {t('dashboard.leanIndicators')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-600 font-medium uppercase">{t('dashboard.score5s')}</p>
                <p className="text-2xl font-bold text-blue-700">{leanStats?.score_5s_global || 0}%</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-xs text-green-600 font-medium uppercase">{t('dashboard.onTimeActions')}</p>
                <p className="text-2xl font-bold text-green-700">{leanStats?.actions_on_time_rate || 0}%</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-purple-50 rounded-lg">
                <p className="text-xs text-purple-600 font-medium uppercase">{t('dashboard.toursRealized')}</p>
                <p className="text-2xl font-bold text-purple-700">{leanStats?.realized_tours || 0}</p>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg">
                <p className="text-xs text-orange-600 font-medium uppercase">{t('dashboard.avgCloseTime')}</p>
                <p className="text-2xl font-bold text-orange-700">{leanStats?.avg_close_time_days || 0}{t('dashboard.days').charAt(0)}</p>
              </div>
            </div>
            {leanStats?.trend_30d && leanStats.trend_30d.length > 0 && (
              <div className="h-20">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={leanStats.trend_30d}>
                    <Line type="monotone" dataKey="score" stroke="#2563EB" strokeWidth={2} dot={false} />
                    <Tooltip formatter={(v) => [`${v}%`, 'Score 5S']} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* TRS/OEE Indicator */}
        <Card className="border-0 shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed'] text-gray-900 tracking-wide flex items-center gap-2">
              <Gauge className="w-5 h-5 text-purple-600" />
              {t('performance.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center py-2">
              <p className={`text-5xl font-bold ${(trsStats?.avg_oee || 0) >= 85 ? 'text-green-600' : (trsStats?.avg_oee || 0) >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                {trsStats?.avg_oee || 0}%
              </p>
              <p className="text-sm text-gray-500 mt-1">{t('performance.globalOEE')}</p>
            </div>
            
            {/* TRS Evolution Chart */}
            {trsStats?.trend_7d && trsStats.trend_7d.some(d => d.oee !== null) && (
              <div className="h-24 mt-2">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trsStats.trend_7d.filter(d => d.oee !== null)}>
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.split('-')[2]} />
                    <YAxis domain={[0, 100]} hide />
                    <Tooltip 
                      formatter={(v) => [`${v}%`, 'TRS']} 
                      labelFormatter={(d) => new Date(d).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="oee" 
                      stroke="#7C3AED" 
                      strokeWidth={2} 
                      dot={{ fill: '#7C3AED', strokeWidth: 0, r: 3 }}
                      activeDot={{ r: 5, fill: '#7C3AED' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
                <p className="text-xs text-center text-gray-400 mt-1">{t('dashboard.trsEvolution7d') || 'Évolution TRS 7 jours'}</p>
              </div>
            )}
            
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-600 font-medium">{t('performance.disp')}</p>
                <p className="text-lg font-bold text-blue-700">{trsStats?.avg_availability || 0}%</p>
              </div>
              <div className="p-2 bg-purple-50 rounded-lg">
                <p className="text-xs text-purple-600 font-medium">{t('performance.perf')}</p>
                <p className="text-lg font-bold text-purple-700">{trsStats?.avg_performance || 0}%</p>
              </div>
              <div className="p-2 bg-green-50 rounded-lg">
                <p className="text-xs text-green-600 font-medium">{t('performance.quality')}</p>
                <p className="text-lg font-bold text-green-700">{trsStats?.avg_quality || 0}%</p>
              </div>
            </div>
            <Link to="/performance">
              <Button variant="outline" size="sm" className="w-full">
                {t('common.seeDetails')} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Safety Indicator */}
        <Card className="border-0 shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed'] text-gray-900 tracking-wide flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-600" />
              {t('safety.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-yellow-50 rounded-lg">
                <p className="text-xs text-yellow-700 font-medium uppercase">{t('safety.nearMisses')}</p>
                <p className="text-2xl font-bold text-yellow-700">{safetyStats?.near_misses || 0}</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="text-xs text-red-600 font-medium uppercase">{t('safety.withStop')}</p>
                <p className="text-2xl font-bold text-red-600">{safetyStats?.accidents_with_stop || 0}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-600 font-medium uppercase">{t('safety.frequencyRate')}</p>
                <p className="text-2xl font-bold text-blue-700">{safetyStats?.frequency_rate || 0}</p>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg">
                <p className="text-xs text-orange-600 font-medium uppercase">{t('safety.daysLost')}</p>
                <p className="text-2xl font-bold text-orange-700">{safetyStats?.days_lost || 0}</p>
              </div>
            </div>
            <Link to="/safety">
              <Button variant="outline" size="sm" className="w-full text-red-600 border-red-200 hover:bg-red-50">
                {t('common.seeDetails')} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Tours Compliance Today */}
      {toursCompliance && toursCompliance.supervisors?.length > 0 && (
        <Card className="border-0 shadow-md" data-testid="tours-compliance">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2 text-gray-900 tracking-wide">
              <ClipboardCheck className="w-6 h-6 text-blue-500" />
              SUIVI DES TOURNÉES DU JOUR
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge className={toursCompliance.compliant_supervisors === toursCompliance.total_supervisors ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                {toursCompliance.compliant_supervisors}/{toursCompliance.total_supervisors} conformes
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {toursCompliance.supervisors.map((supervisor) => (
                <div
                  key={supervisor.user_id}
                  className={`p-4 rounded-xl border-2 ${
                    supervisor.is_compliant 
                      ? 'border-green-200 bg-green-50' 
                      : supervisor.tours_completed > 0 
                        ? 'border-amber-200 bg-amber-50' 
                        : 'border-red-200 bg-red-50'
                  }`}
                  data-testid={`supervisor-compliance-${supervisor.user_id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">{supervisor.user_name}</h4>
                    {supervisor.is_compliant ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <Clock className="w-5 h-5 text-amber-600" />
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${supervisor.is_compliant ? 'bg-green-500' : 'bg-amber-500'}`}
                        style={{ width: `${Math.min(100, supervisor.compliance_rate)}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium">
                      {supervisor.tours_completed}/{supervisor.tours_required}
                    </span>
                  </div>
                  {supervisor.ranks_done?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {supervisor.ranks_done.map(rank => (
                        <Badge key={rank} variant="secondary" className="text-xs">
                          T{rank} ✓
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Urgent Actions */}
      <Card className="border-0 shadow-md" data-testid="urgent-actions">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2 text-gray-900 tracking-wide">
            <AlertTriangle className="w-6 h-6 text-amber-500" />
            {t('dashboard.urgentActions')}
          </CardTitle>
          <Link to="/actions">
            <Button variant="ghost" size="sm" className="text-[#0E346E] font-semibold">
              {t('common.seeAll')} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {stats?.urgent_actions?.length > 0 ? (
            <div className="space-y-3">
              {stats.urgent_actions.map((action) => (
                <div
                  key={action.id}
                  className={`p-4 rounded-xl border-2 ${
                    action.escalation_level >= 3 ? 'escalation-3 border-red-300' :
                    action.escalation_level >= 2 ? 'escalation-2 border-orange-300' :
                    action.escalation_level >= 1 ? 'escalation-1 border-amber-300' : 'border-gray-200 bg-white'
                  }`}
                  data-testid={`urgent-action-${action.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold text-gray-900 truncate">{formatActionTitle(action.title, t)}</h4>
                        {getPriorityBadge(action.priority)}
                        {getStatusBadge(action.status)}
                        {isOverdue(action.deadline) && action.status !== 'FAIT' && (
                          <Badge className="bg-red-600 text-white font-bold animate-pulse">{t('actions.late')}</Badge>
                        )}
                        {action.escalation_level >= 2 && (
                          <Badge className="bg-orange-600 text-white font-bold">{t('actions.escalation')} {action.escalation_level === 3 ? 'J+7' : 'J+3'}</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-2 flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {t('common.deadline')}: {new Date(action.deadline).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <Link to={`/actions?id=${action.id}`}>
                      <Button variant="outline" size="sm" className="border-2 h-10">
                        {t('common.view')}
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <CheckCircle2 className="w-14 h-14 mx-auto mb-3 text-emerald-500" />
              <p className="font-medium">{t('common.noUrgentActions')}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
